import { api, unwrap, type APIResponse } from "./client";
import type { RangeParams } from "./workforce";

// Mirrors app/routers/transfers.py. Every response carries `meta` with the
// display timezone (the single system value — render instants with it, never
// with the browser's zone) and the definition of every figure.

export type TransferOutcome = "merged" | "cancelled" | "no_answer" | "unresolved" | "in_progress";
export type TransferStage = "consulting" | "conferenced" | "handed_off";

export interface TransferMeta {
  timezone: string;
  date_from: string;
  date_to: string;
  /** ISO instant from which consult/conference lengths exist; null if no
   * session has been recorded yet. */
  session_tracking_since: string | null;
  definitions: Record<string, string>;
}

export interface TransferBucket {
  attempts: number;
  customers: number;
  merged: number;
  cancelled: number;
  no_answer: number;
  unresolved: number;
  in_progress: number;
  completion_pct: number | null;
  median_consult_s: number | null;
  median_conference_s: number | null;
}

export interface TransferAgentBucket extends TransferBucket {
  agent_user_id: number | null;
  agent_name: string;
}

export interface TransferDestinationBucket extends TransferBucket {
  destination_name: string;
}

export interface TransferDayBucket extends TransferBucket {
  day: string;
}

export interface TransferAttempt {
  started_at: string;
  agent_user_id: number | null;
  agent_name: string;
  destination_name: string;
  spool_id: number | null;
  outcome: TransferOutcome;
  source: "session" | "events";
  consult_s: number | null;
  conference_s: number | null;
  ended_reason: string | null;
  stage: TransferStage | null;
}

export interface TransfersLive {
  meta: TransferMeta;
  total: TransferBucket;
  by_agent: TransferAgentBucket[];
  by_destination: TransferDestinationBucket[];
  in_progress: TransferAttempt[];
}

export interface TransfersReport {
  meta: TransferMeta;
  total: TransferBucket;
  by_agent: TransferAgentBucket[];
  by_destination: TransferDestinationBucket[];
  by_day: TransferDayBucket[];
  attempts: TransferAttempt[];
}

export async function getTransfersLive(): Promise<TransfersLive> {
  const resp = await api.get<APIResponse<TransfersLive>>("/transfers/live");
  return unwrap(resp.data);
}

export async function getTransfersReport(params: RangeParams): Promise<TransfersReport> {
  const resp = await api.get<APIResponse<TransfersReport>>("/transfers/report", { params });
  return unwrap(resp.data);
}
